import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useT } from "@/lib/i18n";
import { api, errMsg, fmtDate, todayStr } from "@/lib/api";
import { Card, Loader, PageTitle, PositionBadge } from "@/components/kv";

const TYPES = ["annual", "daily", "sick", "other"];

const LeaveDialog = ({ staff, onClose, onSaved }) => {
  const { t } = useT();
  const [f, setF] = useState({ user_id: staff[0]?.id || "", type: "annual", start_date: todayStr(), end_date: todayStr(), note: "" });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    try {
      await api.post("/admin/leaves", f);
      toast.success(t("common.saved"));
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e, t));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#FAF6F0] rounded-2xl" data-testid="leave-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{t("leaves.add")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label>{t("common.staff")}</Label>
            <select className="kv-input" value={f.user_id} onChange={set("user_id")} data-testid="leave-staff-select">
              {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="space-y-1"><Label>{t("leaves.type")}</Label>
            <select className="kv-input" value={f.type} onChange={set("type")} data-testid="leave-type-select">
              {TYPES.map((x) => <option key={x} value={x}>{t(`leaves.types.${x}`)}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>{t("common.start")}</Label><Input type="date" value={f.start_date} onChange={set("start_date")} className="bg-white" data-testid="leave-start-input" /></div>
            <div className="space-y-1"><Label>{t("common.end")}</Label><Input type="date" value={f.end_date} onChange={set("end_date")} className="bg-white" data-testid="leave-end-input" /></div>
          </div>
          <div className="space-y-1"><Label>{t("common.note")}</Label><Input value={f.note} onChange={set("note")} className="bg-white" data-testid="leave-note-input" /></div>
        </div>
        <DialogFooter><Button className="rounded-full" onClick={save} data-testid="leave-save-button">{t("common.save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Leaves() {
  const { t, lang } = useT();
  const [leaves, setLeaves] = useState(null);
  const [staff, setStaff] = useState([]);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    Promise.all([api.get("/admin/leaves"), api.get("/admin/staff")])
      .then(([l, s]) => { setLeaves(l.data); setStaff(s.data); })
      .catch((e) => toast.error(errMsg(e, t)));
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    if (!window.confirm(t("common.confirm_delete"))) return;
    await api.delete(`/admin/leaves/${id}`);
    toast.success(t("common.deleted"));
    load();
  };

  const byId = Object.fromEntries(staff.map((s) => [s.id, s]));
  return (
    <div>
      <PageTitle kicker={t("adminnav.leaves")} title={t("leaves.title")}>
        <Button className="rounded-full" onClick={() => setAdding(true)} disabled={!staff.length} data-testid="leave-add-button"><Plus className="h-4 w-4 mr-1" />{t("leaves.add")}</Button>
      </PageTitle>
      {!leaves ? <Loader /> : (
        <div className="grid lg:grid-cols-[1.5fr_1fr] gap-5">
          <Card className="p-5" data-testid="leaves-list">
            {leaves.length === 0 && <p className="text-sm text-stone-500">{t("leaves.none")}</p>}
            <div className="divide-y divide-[#E8DFD0]">
              {leaves.filter((l) => byId[l.user_id]).map((l) => (
                <div key={l.id} className="py-3 flex items-center justify-between gap-3" data-testid={`leave-row-${l.id}`}>
                  <div>
                    <div className="font-semibold">{byId[l.user_id].name} <span className="ml-1 rounded-full bg-violet-100 text-violet-800 px-2 py-0.5 text-xs">{t(`leaves.types.${l.type}`)}</span></div>
                    <div className="text-sm text-stone-500 capitalize">{fmtDate(l.start_date, lang)} – {fmtDate(l.end_date, lang)}{l.note && ` · ${l.note}`}</div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => remove(l.id)} data-testid={`leave-delete-${l.id}`} aria-label={t("common.delete")}><Trash2 className="h-4 w-4 text-red-700" /></Button>
                </div>
              ))}
            </div>
          </Card>
          <Card className="p-5" data-testid="weekly-off-card">
            <h2 className="font-display text-2xl font-bold mb-3">{t("leaves.weekly")}</h2>
            <div className="space-y-2">
              {staff.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="flex flex-col"><b>{s.name}</b><PositionBadge position={s.position} /></span>
                  <span className="text-right text-stone-600">{s.weekly_off.length ? s.weekly_off.map((d) => t("days_long")[d]).join(", ") : "—"}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
      {adding && <LeaveDialog staff={staff} onClose={() => setAdding(false)} onSaved={load} />}
    </div>
  );
}
